// Eine Zeile in der Sammlungsliste der Einstellungen (Phase 6). Flaeche,
// die es im Original nicht gibt (das Konzept "gemeinsame Sammlung" ist
// eine eigene Erweiterung), deshalb frei gestaltet statt gegen
// Referenz-Screenshots gebaut.

import { useState, type FormEvent } from "react";
import { Check, Copy, LogOut, Pencil, UserPlus, Users } from "lucide-react";
import {
  useEinladungErstellen,
  useMitglieder,
  useSammlungUmbenennen,
  useSammlungVerlassen,
} from "../lib/collections.js";
import { fehlertext } from "../lib/api.js";
import type { Sammlung } from "../lib/me.js";
import { Hinweis, Knopf } from "./Bausteine.js";

export function SammlungsZeile({
  sammlung,
  aktiv,
  onAktivSchalten,
}: {
  sammlung: Sammlung;
  aktiv: boolean;
  onAktivSchalten: () => void;
}) {
  const [aufgeklappt, setAufgeklappt] = useState(false);
  const [einladungslink, setEinladungslink] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [nameAendern, setNameAendern] = useState(false);
  const [name, setzeName] = useState(sammlung.name);

  const mitglieder = useMitglieder(aufgeklappt && sammlung.kind === "shared" ? sammlung.id : undefined);
  const einladungErstellen = useEinladungErstellen(sammlung.id);
  const verlassen = useSammlungVerlassen(sammlung.id);
  const umbenennen = useSammlungUmbenennen(sammlung.id);

  function umbenennenAbsenden(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2 || name.trim() === sammlung.name) {
      setNameAendern(false);
      return;
    }
    umbenennen.mutate(name.trim());
    setNameAendern(false);
  }

  async function einladen() {
    const antwort = await einladungErstellen.mutateAsync();
    setEinladungslink(`${window.location.origin}/sammlung-einladung/${antwort.token}`);
  }

  async function linkKopieren() {
    if (!einladungslink) return;
    await navigator.clipboard.writeText(einladungslink);
    setKopiert(true);
    setTimeout(() => setKopiert(false), 2000);
  }

  return (
    <li className="border-t border-neutral-100 py-2 first:border-t-0">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => (sammlung.kind === "shared" ? setAufgeklappt((v) => !v) : undefined)}
          className="flex min-h-11 flex-1 items-center gap-2 text-left"
        >
          <span className={aktiv ? "font-semibold text-akzent" : ""}>{sammlung.name}</span>
          <span className="text-xs text-neutral-500">
            {sammlung.kind === "personal" ? "persönlich" : sammlung.role === "owner" ? "gemeinsam · von dir" : "gemeinsam"}
          </span>
        </button>
        {!aktiv && (
          <button
            type="button"
            onClick={onAktivSchalten}
            className="min-h-11 shrink-0 rounded border border-neutral-300 px-3 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Aktiv schalten
          </button>
        )}
        {aktiv && <Check size={18} className="shrink-0 text-akzent" aria-label="Aktiv" />}
      </div>

      {aufgeklappt && sammlung.kind === "shared" && (
        <div className="mt-2 rounded bg-neutral-50 p-3 text-sm">
          <div className="mb-2 flex items-center gap-2 text-neutral-600">
            <Users size={16} />
            <span>Mitglieder</span>
          </div>
          <ul className="mb-3 space-y-1">
            {(mitglieder.data ?? []).map((m) => (
              <li key={m.userId} className="flex justify-between">
                <span>{m.displayName}</span>
                <span className="text-neutral-500">{m.role === "owner" ? "Owner" : "Mitglied"}</span>
              </li>
            ))}
          </ul>

          {/* Umbenennen wie der Anzeigename: erlaubt, aber nicht offen
              stehend. Nur der Owner darf es, der Server lehnt alles andere
              mit 403 ab. */}
          {sammlung.role === "owner" &&
            (nameAendern ? (
              <form className="mb-3 flex gap-2" onSubmit={umbenennenAbsenden}>
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setzeName(e.target.value)}
                  maxLength={60}
                  className="min-h-11 min-w-0 flex-1 rounded border border-neutral-300 px-3 text-sm outline-none focus:border-akzent"
                />
                <Knopf
                  type="submit"
                  art="neben"
                  disabled={umbenennen.isPending || name.trim().length < 2}
                  kinder="Speichern"
                />
                <Knopf
                  art="neben"
                  onClick={() => {
                    setzeName(sammlung.name);
                    setNameAendern(false);
                  }}
                  kinder="Abbrechen"
                />
              </form>
            ) : (
              <div className="mb-3">
                <Knopf
                  art="neben"
                  onClick={() => {
                    setzeName(sammlung.name);
                    setNameAendern(true);
                  }}
                  kinder={
                    <span className="flex items-center gap-2">
                      <Pencil size={16} /> Umbenennen
                    </span>
                  }
                />
              </div>
            ))}
          {umbenennen.error && <Hinweis art="fehler" kinder={fehlertext(umbenennen.error)} />}

          {sammlung.role === "owner" ? (
            <div className="flex flex-col gap-2">
              <Knopf art="neben" onClick={() => void einladen()} kinder={
                <span className="flex items-center gap-2">
                  <UserPlus size={16} /> Einladungslink erstellen
                </span>
              } />
              {einladungslink && (
                <div className="flex items-center gap-2 rounded border border-neutral-200 bg-flaeche-inhalt p-2">
                  <input readOnly value={einladungslink} className="min-w-0 flex-1 bg-transparent text-xs" />
                  <button type="button" onClick={() => void linkKopieren()} aria-label="Link kopieren" className="min-h-11 min-w-11">
                    {kopiert ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Knopf
              art="warnung"
              onClick={() => void verlassen.mutateAsync()}
              kinder={
                <span className="flex items-center gap-2">
                  <LogOut size={16} /> Sammlung verlassen
                </span>
              }
            />
          )}
        </div>
      )}
    </li>
  );
}
